import { defineMessages } from 'react-intl';
import { CashFlowCategory, CashFlowType } from '@algoan/dashboard/model';

export const synthesisMessages = defineMessages({
  situation: {
    id: 'widgets.debt_consolidation_synthesis.situation',
    defaultMessage: 'Situation',
    description: 'Title of situation section',
  },
  lastBalance: {
    id: 'widgets.debt_consolidation_synthesis.last_balance',
    defaultMessage: 'Last balance',
    description: 'Title of the line last balance',
  },
  ofWhichSavings: {
    id: 'widgets.debt_consolidation_synthesis.of_which_savings',
    defaultMessage: 'Of which savings',
    description: 'Title of the line of which savings',
  },
  checkingsAccounts: {
    id: 'widgets.debt_consolidation_synthesis.checkings_accounts',
    defaultMessage: 'Checkings accounts',
    description: 'Title of the line checkings accounts',
  },
  savingsAccounts: {
    id: 'widgets.debt_consolidation_synthesis.savings_accounts',
    defaultMessage: 'Savings accounts',
    description: 'Title of the line savings accounts',
  },
  loans: {
    id: 'widgets.debt_consolidation_synthesis.loans',
    defaultMessage: 'Loans',
    description: 'Title of the loans section',
  },
  numberOfLoans: {
    id: 'widgets.debt_consolidation_synthesis.number_of_loans',
    defaultMessage: 'Number of loans',
    description: 'Title of the line number of loans',
  },
  totalMonthlyAmount: {
    id: 'widgets.debt_consolidation_synthesis.total_monthly_amount',
    defaultMessage: 'Total monthly amount',
    description: 'Title of the line total monthly amount',
  },
  indicators: {
    id: 'widgets.general_synthesis.indicators',
    defaultMessage: 'Indicators',
    description: 'Title of indicators section',
  },
  overdraftIndicatorTitle: {
    id: 'widgets.debt_consolidation_synthesis.overdraft_indicator_title',
    defaultMessage: 'Overdraft',
    description: 'Title of overdraft indicator',
  },
  overdraftIndicatorValue: {
    id: 'widgets.debt_consolidation_synthesis.overdraft_indicator_value',
    defaultMessage: '{numberOfDays} {days} with {amount} max',
    description: 'Message of overdraft indicator',
  },
  informationIndicator: {
    id: 'widgets.debt_consolidation_synthesis.information_indicator',
    defaultMessage: '{type} for {amount}',
    description: 'Message of information indicator',
  },
  complexIndicator: {
    id: 'widgets.debt_consolidation_synthesis.complex_indicator',
    defaultMessage: '{numberOfTransactions} {transactions} for {amount}',
    description: 'Message of complex indicator',
  },
  payments: {
    id: 'widgets.debt_consolidation_synthesis.payments',
    defaultMessage: 'Payments',
    description: 'Title of payment indicators',
  },
  days: {
    id: 'widgets.debt_consolidation_synthesis.days',
    defaultMessage: '{days, plural, one {day} other {days}}',
  },
  transactions: {
    id: 'widgets.debt_consolidation_synthesis.transaction',
    defaultMessage: '{transaction, plural, one {transaction} other {transactions}}',
  },
  yes: {
    id: 'widgets.debt_consolidation_synthesis.yes',
    defaultMessage: 'Yes',
  },
  no: {
    id: 'widgets.debt_consolidation_synthesis.no',
    defaultMessage: 'No',
  },
  recurringTransactions: {
    id: 'widgets.debt_consolidation_synthesis.recurring_transactions',
    defaultMessage: 'Recurring transactions',
    description: 'Title of the line recurring transactions',
  },
  contributeToCalculation: {
    id: 'widgets.debt_consolidation_loan_analysis.contribute_to_calculation',
    defaultMessage: 'Contribute to Calculation',
    description: 'Title of the RAC table headings',
  },
  loanConsolidation: {
    id: 'widgets.debt_consolidation_loan_analysis.loan_consolidation',
    defaultMessage: 'Loan Consolidation',
    description: 'Title of the last RAC table headings',
  },
  noTransaction: {
    id: 'widgets.debt_consolidation_loan_analysis.no_transaction',
    defaultMessage: 'No transaction to display',
    description: 'Message when no transaction is displayed',
  },
  locDrawdownTitle: {
    id: 'widgets.cofidis_debt_consolidation_synthesis.loc_drawdown_title',
    defaultMessage: 'LOC drawdown',
    description: 'Title of loc drawdown indicator',
  },
  locDrawdownsOver30DaysTitle: {
    id: 'widgets.cofidis_debt_consolidation_synthesis.loc_drawdown_over_thirty_days_title',
    defaultMessage: 'LOC drawdowns over 30 days',
    description: 'Title of loc drawdown over 30 days indicator',
  },
  locDrawdown: {
    id: 'widgets.debt_consolidation_synthesis.loc_drawdown',
    defaultMessage: '{locDrawdown, plural, one {# loc drawdown} other {# loc drawdowns}}',
    description: 'Title of loc drawdown indicator',
  },
  yesNoIndicatorValue: {
    id: 'widgets.debt_consolidation_synthesis.indicators.explicit_indicator_value.YES_NO',
    defaultMessage:
      '{isTruthy, select, true {<bold>Yes</bold>{count, select, undefined {} other {, <bold>{count}</bold> for {amount}}}} other {<bold>No</bold>}}',
    description: 'Yes/No indicator',
  },
  averageMoney: {
    id: 'widgets.debt_consolidation_synthesis.indicators.explicit_indicator_value.AVERAGE_MONEY',
    defaultMessage: '{averageNumber} for {averageMoney}',
    description: 'Average number indicator',
  },
  typeOfCard: {
    id: 'widgets.debt_consolidation_synthesis.indicators.explicit_indicator_value.CARD_TYPE',
    defaultMessage: '{cardType, select, DIFFERED {Differed} DIRECT {Direct} other {0}}',
    description: 'Card type indicator',
  },
  [CashFlowCategory.DIFFERED_PAYMENT]: {
    id: 'widgets.debt_consolidation_synthesis.differed_debit',
    defaultMessage: 'Differed debit',
    description: 'Title of differed debit indicator',
  },
  [CashFlowType.CHECK]: {
    id: 'widgets.debt_consolidation_synthesis.checks',
    defaultMessage: '{count, plural, one {# check} other {# checks}}',
    description: 'Title of check indicator',
  },
  [CashFlowType.ATM]: {
    id: 'widgets.debt_consolidation_synthesis.withdrawal',
    defaultMessage: '{count, plural, one {# withdrawal} other {# withdrawals}}',
    description: 'Title of withdrawal indicator',
  },
  [CashFlowType.CARD_PAYMENT]: {
    id: 'widgets.debt_consolidation_synthesis.cards',
    defaultMessage: '{count, plural, one {# card} other {# cards}}',
    description: 'Title of card indicator',
  },
  [CashFlowType.DEBIT]: {
    id: 'widgets.debt_consolidation_synthesis.debit',
    defaultMessage: '{count, plural, one {# debit} other {# debits}}',
    description: 'Title of debit indicator',
  },
  [CashFlowType.OUTGOING_TRANSFER]: {
    id: 'widgets.debt_consolidation_synthesis.outgoing_transfer',
    defaultMessage: '{count} outgoing {count, plural, one {transfer} other {transfers}}',
    description: 'Title of outgoing transfer refund indicator',
  },
});
